/**
 * ActionPanel
 * -----------
 * 
 * October 2026
 */

import DistributionSelection from '../distributions/DistributionSelection';
import LoopPanel from './LoopPanel';
import OrPanel from './OrPanel';
import RelationshipPanel from './RelationshipPanel';

import * as React from 'react';

import { Card } from "primereact/card";
import { Checkbox } from 'primereact/checkbox';
import { Divider } from 'primereact/divider';
import { Dropdown } from 'primereact/dropdown';
import { InputNumber } from 'primereact/inputnumber';
import { InputText } from 'primereact/inputtext';
import { Paginator } from "primereact/paginator";

const actionWarnings = {
    'name': function(newName) { return newName === "" ? "Name is required" : null }
};

/**
 * Configuration sub-panel for editing an action's attributes.
 * 
 * @param {Object} config               - Configuration object for the simulation model. 
 * @param {Function} setConfig          - Setter for config object.
 * @param {Object} action               - Attributes for action element being edited.
 * @param {Object} configWarnings       - Verification warnings for action elements.
 * @param {Function} setConfigWarnings  - Setter for verification warnings.
 * @param {Boolean} reachedConfig       - Flag for when users enter the configuration panel.
 * 
 * @todo Refactor verification handling for attributes applying distribution components.
 */
function ActionPanel({ config, setConfig, action, configWarnings, setConfigWarnings, reachedConfig }) {
    if (!action) return null;

    // Manages current section index to display.
    const [sectionIndex, setSectionIndex] = React.useState(0);

    // Manages the current resource relationship being rendered.
    const [selectedResGid, setResGid] = React.useState("");

    /**
     * Resets attribute panel to the "Basic Info" section.
     * Triggers when current action element switches.
     */
    React.useEffect(() => {
        setSectionIndex(0);
    }, [action.gid]);

    // Verifies that the or element's probabilities were set correctly once an action diagram is initially parsed.
    React.useEffect(() => {
        if (reachedConfig) {
            console.log("Verifying action elements.");
            for (const [gid, action] of Object.entries(config.actions)) {
                if (action.type === 'or') {
                    if (action.or_script.type === 'probability') {
                        console.log("Analyzing OR element's probability script.");
                        const probabilitySum = action.or_script.decision.reduce((total, currentProb) => total + currentProb, 0);
                        updateActionWarnings(gid, 'probability_script', probabilitySum !== 100 ? "Probabilities must sum to 100" : null);
                    } else {
                        updateActionWarnings(gid, 'probability_script', null);
                    }
                }
            }
        }
    // eslint-disable-next-line
    }, [reachedConfig]);

    /**
     * Updates warnings for an action element's parameters.
     * 
     * @param {String} gid          - Action's identifier.
     * @param {String} parameter    - Element parameter linked to warning.
     * @param {String} warning      - Updated warning message.
     */
    const updateActionWarnings = (gid, parameter, warning) => {
        setConfigWarnings(prev => {
            const newWarnings = {...prev, actions: {...prev.actions, [gid]: {...prev.actions[gid]}}};
            if (warning) {
                newWarnings.actions[gid][parameter] = warning;
            } else {
                delete newWarnings.actions[gid][parameter];
            }
            return newWarnings;
        });
    }

    /**
     * Updates the action's name.
     * 
     * @param {String} newName  - Updated name.
     * @param {String} gid      - Action's identifier.
     */
    const handleActionName = (newName, gid) => {
        updateActionWarnings(gid, 'name', actionWarnings['name'](newName));

        const newConfig = {...config, actions: {...config.actions, [gid]: {...config.actions[gid], name: newName}}};
        setConfig(newConfig);
    }

    /**
     * Updates the action's time unit.
     * 
     * @param {String} newUnits - Updated time unit.
     * @param {String} gid      - Action's identifier.
     */
    const handleActionUnits = (newUnits, gid) => {
        const newConfig = {...config, actions: {...config.actions, [gid]: {...config.actions[gid], units: newUnits}}};
        setConfig(newConfig);
    }

    /**
     * Toggles the use of entity grouping for an action element.
     * 
     * @param {Boolean} checked - Flag for enabling/disabling entity grouping.
     * @param {String} gid      - Action's identifier.
     */
    const handleGroupingState = (checked, gid) => {
        const newAction = {...action, grouping_enabled: checked};
        const newActions = {...config.actions, [gid]: newAction};
        const newConfig = {...config, actions: newActions};
        setConfig(newConfig);
    }

    /**
     * Updates action's attribute with a probability distribution.
     * 
     * Example attributes:
     *  - time duration
     * 
     * @param {String} attr     - Attribute key of the action.
     * @param {Object} newDist  - Updated distribution parameters.
     */
    const updateActionDistribution = (attr, newDist) => {
        const newAction = {...action, [attr]: newDist};
        const newActions = {...config.actions, [action.gid]: newAction};
        const newConfig = {...config, 'actions': newActions};
        setConfig(newConfig);
    }

    /**
     * New index for a panel section.
     * 
     * @param {Number} newSection 
     */
    const handleSectionChange = (newSection) => {
        setSectionIndex(newSection);
    }

    /**
     * Panel section for an action element's basic attributes.
     * 
     * @returns Child component for the "Basic Info" section.
     */
    const basicInfoSection = () => {
        return (
            <div>
                <div>
                    <label>Gid: {action.gid}</label>
                </div>
                <Divider/>
                <div>
                    <label style={{ marginRight: '1rem'}}>Name:</label>
                    <InputText value={action.name} onChange={(event) => handleActionName(event.target.value, action.gid)} placeholder="Enter name" invalid={!!configWarnings.actions?.[action.gid]?.name} style={{ width: '150px', marginRight: '0.5rem'}}/>
                </div>
                <Divider/>
                <div>
                    <label style={{ marginRight: '1rem'}}>Units:</label>
                    <Dropdown value={action.units} onChange={(event) => handleActionUnits(event.target.value, action.gid)} options={["seconds","minutes","hours","days","weeks","months", "years"]}/>
                </div>
                <Divider/>
                <DistributionSelection elementType={'actions'} element={action} attr={'time'} dist={action.time.dist} attrLabel={"Duration time"} distUpdater={updateActionDistribution} warningUpdater={updateActionWarnings} configWarnings={configWarnings}/>
            </div>
        )
    }

    /**
     * Panel section for an action element's entity grouping attributes.
     * 
     * @returns Child component for the "Entity Grouping" section.
     */
    const entityGroupSection = () => {
        return (
            <div>
                <div style={{ marginBottom: '1rem'}}>
                    <Checkbox checked={action.grouping_enabled} onChange={(event) => handleGroupingState(event.checked, action.gid)} style={{ marginRight: '0.5rem' }}></Checkbox>
                    <label>Enable Grouping:</label>
                </div>
                {action.grouping_enabled && <DistributionSelection elementType={'actions'} element={action} attr={'group_min'} dist={action.group_min.dist} attrLabel={"Entity Minimum"} distUpdater={updateActionDistribution} warningUpdater={updateActionWarnings} configWarnings={configWarnings}/>}
            </div>
        )
    }

    /**
     * Panel section for an act element's "LOOP" attributes.
     * 
     * @returns Child component for the "Loop Properties" section.
     */
    const loopSection = () => {
        return <LoopPanel config={config} setConfig={setConfig} action={action} configWarnings={configWarnings} updateActionWarnings={updateActionWarnings}/>;
    }

    /**
     * Panel section for an act element's "OR" attributes.
     * 
     * @returns Child component for the "Or Properties" section.
     */
    const orSection = () => {
        return <OrPanel config={config} setConfig={setConfig} action={action} configWarnings={configWarnings} updateActionWarnings={updateActionWarnings} reachedConfig={reachedConfig}/>;
    }

    /**
     * Panel section for an action element's resource relationships.
     * 
     * @returns Child component for the "Resourcet Relationships" section.
     */
    const relationshipSection = () => {
        return <RelationshipPanel config={config} setConfig={setConfig} action={action} selectedResGid={selectedResGid} setResGid={setResGid} configWarnings={configWarnings} updateActionWarnings={updateActionWarnings}/>;
    }

    const sections = [basicInfoSection(), entityGroupSection()];
    const dropdownSections = [
        { label: "Basic Info", value: 0 },
        { label: "Entity Grouping", value: 1 }
    ];

    if (action.type === "loop") {
        sections.push(loopSection());
        dropdownSections.push({ label: "Loop Properties", value: dropdownSections.length});
    } else if (action.type === "or") {
        sections.push(orSection());
        dropdownSections.push({ label: "Or Properties", value: dropdownSections.length});
    }

    if (Object.keys(action.resources).length > 0) {
        sections.push(relationshipSection());
        dropdownSections.push({ label: "Resource Relationships", value: dropdownSections.length});
    }

    return (
        <Card style={{ width: "460px", height: "530px" }}>
            <Dropdown value={sectionIndex} onChange={(event) => setSectionIndex(event.value)} options={dropdownSections} optionLabel="label" optionValue="value" style={{ marginBottom: "2rem" }}
                valueTemplate={(option) => (<span style={{ fontSize: "1.17em", fontWeight: "bold" }}>{option?.label}</span>)}/>
            <div style={{ height: "330px", overflow: "auto" }}>{sections[sectionIndex]}</div>
            <Paginator first={sectionIndex} rows={1} totalRecords={dropdownSections.length} onPageChange={(event) => handleSectionChange(event.first)} template={{ layout: 'PrevPageLink CurrentPageReport NextPageLink' }}/>
        </Card>
    )
}

export default ActionPanel;